import axios from 'axios';
import URLUtil from '../../utils/URLUtil';
import AppConfig from '../../AppConfig.js';
import {booking} from '../booking/feature.booking.svelte.js';
import * as booking_actions from '../booking/actions_booking.svelte.js';
import * as booking_update from '../booking/booking.scenarios.update.svelte.js';
import * as debug_actions from '../debug/actions_debug.svelte.js';
import * as booking_select from '../booking/booking.scenarios.select.svelte.js';

const AVAILABILITY_WEEKDAYS = 'weekdays';
const AVAILABILITY_WEEKDAYS_AND_WEEKENDS = 'weekdays_and_weekends';
const AVAILABILITY_ALL_DAYS = 'all_days';

/**
 * Main entry point called by the DiscountCodeInput component.
 * If the code matches a 13-digit gift card pattern, delegates to the gift card flow.
 * Otherwise validates the code against the discount code backend endpoint.
 *
 * @param {string} sCode - The code entered by the user
 * @returns {Promise<{bIsSuccess: boolean, sErrorMessage?: string, bIsClaimed?: boolean, sAvailableAfter?: string}>}
 */
export async function validateCode(sCode)
{
  debug_actions.debugAddLogFlow('discount', 'validateCode', {sCode});

  // Shared rate-limit guard: refuse before hitting any backend endpoint
  if ( booking.bIsCodeRateLimited )
  {
    return {
      bIsSuccess: false,
      sErrorMessage: 'Te veel ongeldige pogingen. Probeer het later opnieuw.'
    };
  }

  // 13-digit codes are gift cards — delegate to existing gift card flow
  if ( /^\d{13}$/.test(sCode) )
  {
    booking_actions.setGiftCard(sCode);
    return {bIsSuccess: true};
  }

  if ( AppConfig.bDebugUseFakeAjaxCalls )
  {
    return await _validateCodeFake(sCode);
  }

  const sBaseUrl = URLUtil.getCurrentPageRelativeLinkWithoutAction();
  const sTargetUrl = sBaseUrl + '/resdata/validateDiscountCode/';

  try
  {
    const oResponse = await axios.post(sTargetUrl, {sCode});
    const oData = oResponse.data;

    if ( oData.bIsValid )
    {
      _applyValidatedDiscountCode(oData);
      return {bIsSuccess: true};
    }
    else
    {
      if ( oData.bIsRateLimited )
      {
        booking.bIsCodeRateLimited = true;
      }

      return {
        bIsSuccess: false,
        sErrorMessage: oData.sErrorMessage || 'Deze kortingscode is niet geldig.',
        bIsClaimed: oData.bIsClaimed || false,
        sAvailableAfter: oData.sAvailableAfter || null
      };
    }
  }
  catch (oError)
  {
    console.error('Error validating discount code:', oError);
    return {
      bIsSuccess: false,
      sErrorMessage: 'Er is een fout opgetreden bij het valideren van de kortingscode.'
    };
  }
}

/**
 * Removes a validated discount code from the booking by its code string.
 * Triggers receipt recalculation after removal.
 * If the removed code was a replacement-type discount, also triggers entry reevaluation.
 *
 * @param {string} sCode - The discount code to remove
 */
export function removeDiscountCode(sCode)
{
  debug_actions.debugAddLogFlow('discount', 'removeDiscountCode', {sCode});

  const oCodeBeingRemoved = booking.lValidDiscountCodes.find(oCode => oCode.sCode === sCode);
  const bWasReplacementDiscount = oCodeBeingRemoved?.bReplacesEntryProduct;

  booking.lValidDiscountCodes = booking.lValidDiscountCodes.filter(
    oDiscountCode => oDiscountCode.sCode !== sCode
  );

  if (bWasReplacementDiscount) {
    booking_update.evaluateCurrentEntryProductsAfterTimeOrPaymentMethodChange();

    // Reset fixed person count if the removed code was setting it
    if (oCodeBeingRemoved?.iMaxPersons > 0) {
      booking.iFixedNrOfPeople = false;
    }
  }

  booking_actions.handleBookingUpdated('removeDiscountCode');
}

/**
 * Returns the most restrictive calendar availability constraint from all validated discount codes.
 * Restriction order (most → least restrictive): weekdays < weekdays_and_weekends < all_days
 * Returns null when no discount codes are active (no filtering applied).
 *
 * @returns {string|null} The most restrictive sAvailability value, or null if no codes
 */
export function getDiscountCalendarAvailability()
{
  const aValidCodes = booking.lValidDiscountCodes;

  if ( aValidCodes.length === 0 )
  {
    return null;
  }

  const aAvailabilityOrder = [
    AVAILABILITY_WEEKDAYS,
    AVAILABILITY_WEEKDAYS_AND_WEEKENDS,
    AVAILABILITY_ALL_DAYS
  ];

  let iMostRestrictiveIndex = aAvailabilityOrder.length - 1;

  for ( const oCode of aValidCodes )
  {
    const iIndex = aAvailabilityOrder.indexOf(oCode.sAvailability);
    if ( iIndex !== -1 && iIndex < iMostRestrictiveIndex )
    {
      iMostRestrictiveIndex = iIndex;
    }
  }

  return aAvailabilityOrder[iMostRestrictiveIndex];
}

/**
 * Assigns discount codes to persons using priority rules.
 * Called FROM the receipt recalculation scenario — does NOT trigger receipt recalc itself.
 *
 * Priority rules:
 * - If a package deal is active, returns an empty assignments array (discounts don't apply)
 * - Codes with iMaxPersons = 0 create as many slots as there are persons (unlimited)
 * - Codes with iMaxPersons > 0 create that many slots
 * - Slots are sorted: highest percentage first, then generic before unique at equal percentage
 * - One slot is assigned per person (up to the number of persons)
 *
 * @param {Array<object>} aPeople - Array of person objects from the booking
 * @param {number} iEntryPriceCents - Entry price per person in cents
 * @returns {Array<{iPersonIndex: number, sCode: string, iDiscountPercentage: number, iDiscountAmountCents: number, iElliDiscountArticleID: number}>}
 */
export function assignDiscountsToPersons(aPeople, iEntryPriceCents)
{
  debug_actions.debugAddLogFlow('discount', 'assignDiscountsToPersons', {aPeople, iEntryPriceCents});

  // Discounts don't apply when a package deal is active
  if ( booking_select.getPersonWithPackageDeal() )
  {
    booking.lDiscountAssignments = [];
    return [];
  }

  const iNrOfPeople = aPeople.length;
  const aValidCodes = booking.lValidDiscountCodes;

  if ( aValidCodes.length === 0 || iNrOfPeople === 0 )
  {
    booking.lDiscountAssignments = [];
    return [];
  }

  // Expand each discount code into individual slots
  const aSlots = [];
  for ( const oCode of aValidCodes )
  {
    const iSlotCount = oCode.iMaxPersons === 0 ? iNrOfPeople : oCode.iMaxPersons;
    for ( let iSlotIndex = 0; iSlotIndex < iSlotCount; iSlotIndex++ )
    {
      aSlots.push({
        sCode: oCode.sCode,
        sCampaignType: oCode.sCampaignType,
        iDiscountPercentage: oCode.iDiscountPercentage,
        iElliDiscountArticleID: oCode.iElliDiscountArticleID || 0,
        bReplacesEntryProduct: oCode.bReplacesEntryProduct || false,
        iReplacementEntreeExternalID: oCode.iReplacementEntreeExternalID || null
      });
    }
  }

  // Sort slots: highest percentage first, then generic before unique at equal percentage
  aSlots.sort((oSlotA, oSlotB) => {
    if ( oSlotB.iDiscountPercentage !== oSlotA.iDiscountPercentage )
    {
      return oSlotB.iDiscountPercentage - oSlotA.iDiscountPercentage;
    }
    // At equal percentage: generic (0) sorts before unique (1)
    const iTypeA = oSlotA.sCampaignType === 'generic' ? 0 : 1;
    const iTypeB = oSlotB.sCampaignType === 'generic' ? 0 : 1;
    return iTypeA - iTypeB;
  });

  // Assign one slot per person
  const aAssignments = [];
  for ( let iPersonIndex = 0; iPersonIndex < iNrOfPeople; iPersonIndex++ )
  {
    if ( iPersonIndex >= aSlots.length )
    {
      break;
    }

    const oSlot = aSlots[iPersonIndex];
    // Replacement-type discounts don't create a monetary discount line (discount is implicit in replacement product price)
    const iDiscountAmountCents = oSlot.bReplacesEntryProduct
      ? 0
      : Math.round(iEntryPriceCents * oSlot.iDiscountPercentage / 100);

    aAssignments.push({
      iPersonIndex,
      sCode: oSlot.sCode,
      iDiscountPercentage: oSlot.iDiscountPercentage,
      iDiscountAmountCents,
      iElliDiscountArticleID: oSlot.iElliDiscountArticleID,
      bReplacesEntryProduct: oSlot.bReplacesEntryProduct,
      iReplacementEntreeExternalID: oSlot.iReplacementEntreeExternalID
    });
  }

  booking.lDiscountAssignments = aAssignments;
  return aAssignments;
}

/**
 * Applies a validated discount code response object to the booking state.
 * Prevents duplicate codes and triggers receipt recalculation.
 * If the code uses entry replacement mode, also triggers entry reevaluation.
 *
 * @param {object} oValidatedCode - The validated code data returned by the backend
 */
function _applyValidatedDiscountCode(oValidatedCode)
{
  const bIsAlreadyAdded = booking.lValidDiscountCodes.some(
    oExistingCode => oExistingCode.sCode === oValidatedCode.sCode
  );

  if ( bIsAlreadyAdded )
  {
    return;
  }

  booking.lValidDiscountCodes = [
    ...booking.lValidDiscountCodes,
    {
      sCode: oValidatedCode.sCode,
      sCampaignType: oValidatedCode.sCampaignType,
      iDiscountPercentage: oValidatedCode.iDiscountPercentage,
      sAvailability: oValidatedCode.sAvailability,
      iMaxPersons: oValidatedCode.iMaxPersons,
      iElliDiscountArticleID: oValidatedCode.iElliDiscountArticleID || 0,
      bReplacesEntryProduct: oValidatedCode.bReplacesEntryProduct || false,
      iReplacementEntreeExternalID: oValidatedCode.iReplacementEntreeExternalID || null
    }
  ];

  // Handle fixed person count for entry replacement discount codes
  console.log('oValidatedCode.iMaxPersons:', oValidatedCode.iMaxPersons);
  if (oValidatedCode.bReplacesEntryProduct === true && oValidatedCode.iMaxPersons > 0)
  {
    const iFixedPersonCount = oValidatedCode.iMaxPersons;

    // Set fixed count to skip Step17 in navigation
    booking.iFixedNrOfPeople = iFixedPersonCount;

    // Adjust lPeople to match the fixed count
    // Step10: lPeople empty -> populates with fixed count
    // FormPayment: lPeople populated -> adjusts to exact count (removes excess)
    booking_actions.setNrOfPeople(iFixedPersonCount);
  }

  // Always reevaluate entry products after a discount code is accepted
  // This ensures replacement discounts work even when entering code at Afronden step
  booking_update.evaluateCurrentEntryProductsAfterTimeOrPaymentMethodChange();

  booking_actions.handleBookingUpdated('validateCode');
}

/**
 * Fake implementation of validateCode for local development.
 * Returns a successful response for code 'TEST10', a rate-limited error for 'RATELIMIT',
 * and a generic error for all other codes.
 *
 * @param {string} sCode
 * @returns {Promise<object>}
 */
async function _validateCodeFake(sCode)
{
  return new Promise((resolve) => {
    setTimeout(() => {
      if ( sCode === 'TEST10' )
      {
        _applyValidatedDiscountCode({
          bIsValid: true,
          sCode: 'TEST10',
          sCampaignType: 'generic',
          iDiscountPercentage: 10,
          sAvailability: AVAILABILITY_WEEKDAYS_AND_WEEKENDS,
          iMaxPersons: 0,
          iElliDiscountArticleID: 0
        });
        resolve({bIsSuccess: true});
      }
      else if ( sCode === 'RATELIMIT' )
      {
        booking.bIsCodeRateLimited = true;
        resolve({
          bIsSuccess: false,
          sErrorMessage: 'Te veel pogingen. Probeer het later opnieuw.',
          bIsClaimed: false,
          sAvailableAfter: null
        });
      }
      else
      {
        resolve({
          bIsSuccess: false,
          sErrorMessage: 'Deze kortingscode is niet geldig.',
          bIsClaimed: false,
          sAvailableAfter: null
        });
      }
    }, 800);
  });
}
